import { Provider } from "react-redux";
import { Outlet } from "react-router-dom";

import { CrmSessionBootstrap } from "@crm-src/CrmRoot";
import WebSocketProvider from "@crm-src/components/websocket-provider";
import { store } from "@crm-src/lib/store";

/**
 * Mount point for the email-campaign pages inside the Marketing section.
 *
 * The email-campaign feature is a CRM-sub-app feature (it talks to
 * crm-api for campaigns and reads contacts + contact-groups from there)
 * and was relocated OUT of `/crm` into `/marketing/campaigns` on
 * 2026-06-02, next to the autonomous marketing agent. It therefore needs
 * the SAME runtime the `/crm` tree provides — the CRM Redux store, a
 * freshly-minted crm-api session, and the WebSocket provider
 * (`EmailCampaignsPage` uses `useWebSocket()`) — but WITHOUT the CRM
 * sidebar/chrome (the Marketing shell wraps it instead). It does NOT mount
 * a `<Toaster>`: the shared toast store is rendered once by AppShell, which
 * this route is nested under. `CrmSessionBootstrap` is exported by `CrmRoot`
 * for exactly this "lift a CRM page outside /crm" case; the store is a
 * shared module-level singleton and the bootstrap always mints a fresh
 * short-TTL token, so mounting it here is safe (only one of `/crm` or
 * `/marketing/campaigns` is mounted at a time).
 */
export function MarketingCampaignsRoot(): JSX.Element {
  return (
    <Provider store={store}>
      <CrmSessionBootstrap>
        <WebSocketProvider>
          <Outlet />
          {/* No <Toaster> here (removed 2026-09-12, same fix as CrmRoot). This
              route is nested under the AppShell layout route, which mounts
              the ONE toaster over the ONE shared toast store — a second one
              here painted every toast twice, stacked (Cypress saw the top
              copy "covering" the bottom one). */}
        </WebSocketProvider>
      </CrmSessionBootstrap>
    </Provider>
  );
}
